/**
 * Multi-rol — lógica pura (sin Supabase) para elegir el ROL ACTIVO de la sesión.
 *
 * Modelo (contrato multi-rol):
 *   · `usuarios.rol_id` = rol ACTIVO (lo usan RLS, submenus_permisos, menús…).
 *   · `user_roles` (activo = true) = TODOS los roles asignados; `es_principal`
 *     marca el rol por defecto.
 *   · El usuario solo puede cambiar su rol activo con la RPC
 *     `seleccionar_rol_activo(p_rol_id)` (SECURITY DEFINER): el trigger
 *     `usuarios_bloquea_autoescalada` rechaza el `UPDATE usuarios SET rol_id`
 *     hecho desde el cliente.
 *
 * Vive aparte de las llamadas a BD para poder probarla en vitest (entorno node).
 */

export const ROL_AGENTE_INMOBILIARIO = 3;
export const ROL_INMOBILIARIA = 4;
export const ROL_AGENTE_INTERNO = 9;
export const ROL_CLIENTE = 23;
export const ROL_EMBAJADOR = 25;
export const ROL_SOCIO = 36;
export const ROL_PROSPECTO = 43;

/**
 * Roles que NO se ofrecen en el selector de admin.sozu.com: cada uno tiene su
 * propio host/app (clientes, socios) y entrar al admin con ellos solo lleva a
 * "Acceso No Autorizado" o al portal equivocado.
 *
 * Los de agente (3/9) SÍ se ofrecen: `rutaDeRol` los manda a su host
 * (agentes.sozu.com), no a `/admin`. Antes se excluían y un usuario con tres
 * roles (p. ej. Representante + Agente + Embajador) solo veía dos en el selector.
 */
export const ROLES_EXCLUIDOS_SELECTOR_ADMIN: readonly number[] = [
  ROL_CLIENTE,
  ROL_SOCIO,
  ROL_PROSPECTO,
];

/** Roles que habilitan el portal de agentes (agentes.sozu.com). */
export const ROLES_PORTAL_AGENTES: readonly number[] = [ROL_AGENTE_INMOBILIARIO, ROL_AGENTE_INTERNO];
/** Roles que habilitan el portal de clientes (clientes.sozu.com). */
export const ROLES_PORTAL_CLIENTES: readonly number[] = [ROL_CLIENTE];

export interface RolAsignado {
  rol_id: number;
  nombre: string;
  es_principal: boolean;
  /**
   * `user_roles.id_persona_contexto`: persona que la cuenta REPRESENTA con este
   * rol activo (hoy solo rol 4 → la empresa inmobiliaria). null/undefined = la
   * persona propia. Opcional: BDs/edges viejas no lo traen.
   */
  id_persona_contexto?: number | null;
  /** Nombre de esa persona (empresa), si se pudo resolver. */
  persona_contexto_nombre?: string | null;
}

/**
 * Nombre para mostrar de un rol: "Inmobiliaria · Sozu" cuando el rol tiene
 * persona de contexto; si solo se conoce el id, "Inmobiliaria · persona #186";
 * sin contexto, el nombre del rol tal cual.
 */
export function etiquetaRol(rol: {
  nombre: string;
  id_persona_contexto?: number | null;
  persona_contexto_nombre?: string | null;
}): string {
  const empresa = rol.persona_contexto_nombre?.trim();
  if (empresa) return `${rol.nombre} · ${empresa}`;
  if (rol.id_persona_contexto != null) return `${rol.nombre} · persona #${rol.id_persona_contexto}`;
  return rol.nombre;
}

/**
 * Une las filas de `user_roles` con el rol activo (`usuarios.rol_id`), sin
 * duplicados. El contrato garantiza que el rol activo siempre tiene fila, pero
 * mientras el backfill no corra en un ambiente puede faltar: sin esta unión el
 * selector ofrecería solo el rol adicional y el login lo activaría directo,
 * dejando al usuario sin su rol de siempre. Si ninguna fila es principal, el
 * rol activo hace de principal.
 */
export function normalizarRoles(
  filas: RolAsignado[],
  rolActivo: number | null | undefined,
  nombreRolActivo?: string | null,
): RolAsignado[] {
  const porId = new Map<number, RolAsignado>();
  for (const f of filas) {
    const previo = porId.get(f.rol_id);
    porId.set(f.rol_id, {
      ...previo,
      ...f,
      es_principal: f.es_principal || (previo?.es_principal ?? false),
      id_persona_contexto: f.id_persona_contexto ?? previo?.id_persona_contexto ?? null,
      persona_contexto_nombre: f.persona_contexto_nombre ?? previo?.persona_contexto_nombre ?? null,
    });
  }
  if (rolActivo != null && !porId.has(rolActivo)) {
    porId.set(rolActivo, {
      rol_id: rolActivo,
      nombre: nombreRolActivo || `Rol ${rolActivo}`,
      es_principal: false,
    });
  }
  const lista = [...porId.values()];
  if (rolActivo != null && !lista.some((r) => r.es_principal)) {
    return lista.map((r) => (r.rol_id === rolActivo ? { ...r, es_principal: true } : r));
  }
  return lista;
}

/** Principal primero; el resto por nombre (orden estable para el selector). */
export function ordenarRoles(roles: RolAsignado[]): RolAsignado[] {
  return [...roles].sort((a, b) => {
    if (a.es_principal !== b.es_principal) return a.es_principal ? -1 : 1;
    return a.nombre.localeCompare(b.nombre, 'es');
  });
}

/** Roles elegibles para entrar a admin.sozu.com (ya ordenados). */
export function rolesElegiblesAdmin(roles: RolAsignado[]): RolAsignado[] {
  return ordenarRoles(roles.filter((r) => !ROLES_EXCLUIDOS_SELECTOR_ADMIN.includes(r.rol_id)));
}

/**
 * ¿La cuenta es SOLO agente externo (rol 3) para efectos del admin? Es decir, el
 * rol activo es el 3 y ningún otro rol asignado (fuera de los excluidos del
 * selector: Cliente, Socio, Prospecto) le da algo que hacer en admin.sozu.com.
 *
 * El portal de agentes dentro del admin (`/admin/agent`) está deprecado; a esta
 * cuenta se le manda a agentes.sozu.com en vez de dejarla en los menús viejos
 * del rol 3 (Prospectos). El Agente Interno (9) no entra aquí: sí trabaja en el
 * admin (Inventarios, CRM, Legal Flow…).
 */
export function esSoloAgenteExterno(
  rolActivo: number | null | undefined,
  rolesAsignados: readonly number[],
): boolean {
  if (rolActivo !== ROL_AGENTE_INMOBILIARIO) return false;
  return rolesAsignados.every(
    (r) => r === ROL_AGENTE_INMOBILIARIO || ROLES_EXCLUIDOS_SELECTOR_ADMIN.includes(r),
  );
}

export type DecisionRolAdmin =
  | { tipo: 'selector'; opciones: RolAsignado[] }
  | { tipo: 'activar'; rol: RolAsignado }
  | { tipo: 'ninguna' };

/**
 * Qué hacer tras autenticar en admin.sozu.com:
 *   · >1 rol elegible → selector.
 *   · 1 rol elegible distinto del activo → activarlo sin preguntar.
 *   · 0, o el único ya es el activo → nada (flujo de siempre).
 *
 * Excepción: si el único elegible es Embajador (25) o un rol de agente (3/9)
 * no se "activa". Para Embajador: el
 * embajador es rol dual que nunca cambia `usuarios.rol_id` (entra al portal por
 * `user_roles`), y mandar directo a portal-embajador a un Cliente-embajador le
 * cambiaría el destino de siempre sin haberlo elegido.
 */
export function decidirRolAdmin(roles: RolAsignado[], rolActivo: number | null | undefined): DecisionRolAdmin {
  const opciones = rolesElegiblesAdmin(roles);
  if (opciones.length > 1) return { tipo: 'selector', opciones };
  if (opciones.length === 1) {
    const unico = opciones[0];
    // Los roles de portal (embajador, agentes) tampoco se activan solos: un
    // Cliente que además es agente seguiría yendo a su destino de siempre.
    const esRolDePortal = unico.rol_id === ROL_EMBAJADOR || ROLES_PORTAL_AGENTES.includes(unico.rol_id);
    if (unico.rol_id !== rolActivo && !esRolDePortal) return { tipo: 'activar', rol: unico };
  }
  return { tipo: 'ninguna' };
}

/**
 * Rol a activar al entrar a un host de portal (agentes / clientes). null si el
 * rol activo ya es uno de los candidatos o el usuario no tiene ninguno asignado
 * (entonces sigue el flujo de siempre, incluido el modo administración).
 * Preferencia: el principal si es candidato; si no, el orden de `candidatos`.
 */
export function elegirRolPortal(
  roles: RolAsignado[],
  rolActivo: number | null | undefined,
  candidatos: readonly number[],
): number | null {
  if (rolActivo != null && candidatos.includes(rolActivo)) return null;
  const asignados = roles.filter((r) => candidatos.includes(r.rol_id));
  if (asignados.length === 0) return null;
  const principal = asignados.find((r) => r.es_principal);
  if (principal) return principal.rol_id;
  for (const c of candidatos) {
    if (asignados.some((r) => r.rol_id === c)) return c;
  }
  return null;
}

interface ErrorLike {
  code?: string | null;
  status?: number | null;
  message?: string | null;
}

/**
 * La RPC todavía no existe en esta BD (DDL pendiente): PostgREST responde
 * PGRST202 (no está en el schema cache) / 404, Postgres 42883.
 */
export function esErrorRpcInexistente(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as ErrorLike;
  return (
    e.code === 'PGRST202' ||
    e.code === '42883' ||
    e.status === 404 ||
    /could not find the function/i.test(e.message ?? '')
  );
}

/** La RPC rechazó el rol: no está activo en `user_roles` para el usuario. */
export function esErrorRolNoAsignado(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as ErrorLike).code === '42501';
}

/**
 * La columna pedida en un select directo aún no existe en esta BD (DDL
 * pendiente): Postgres 42703 (undefined_column) o PostgREST PGRST204.
 */
export function esErrorColumnaInexistente(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as ErrorLike;
  return (
    e.code === '42703' ||
    e.code === 'PGRST204' ||
    /column .* does not exist|could not find the .* column/i.test(e.message ?? '')
  );
}
